import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../../hooks/useStore';
import CommonButton from '../../../../../components/common-components/button/CommonButton';
import { FormikDropdown } from '../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import styles from './SwitchHospital.module.css';
import {
  SwitchHospitalFormValues,
  switchHospitalInitialValues,
} from '../../../../../models/hospital/HospitalModel';
import { SwitchHospitalSchema } from '../../../../../schemas/SwitchHospitalSchema';

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<SwitchHospitalFormValues>();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton type="save" onClick={submitForm} disabled={isSubmitting}>
        Kaydet
      </CommonButton>
    </>
  );
};

const HospitalTagSync = () => {
  const { values, setFieldValue } = useFormikContext<SwitchHospitalFormValues>();
  const { hospitalStore, hospitalFilesStore } = useStore();

  useEffect(() => {
    if (values.hospitalId) {
      const selectedHospital = hospitalStore.hospitalList.find(h => h.id === values.hospitalId);
      if (selectedHospital) {
        hospitalFilesStore.getTagsByHospitalId(selectedHospital.id);
        setFieldValue('tagId', null);
      }
    }
  }, [values.hospitalId, hospitalStore.hospitalList, hospitalFilesStore, setFieldValue]);

  return null;
};

const SwitchHospital = observer(() => {
  const { popupStore, hospitalFilesStore, hospitalStore, uiStore } = useStore();

  const tagOptions = useMemo(
    () =>
      hospitalFilesStore.tags.map(tag => ({
        value: tag.id,
        label: tag.prefix,
      })),
    [hospitalFilesStore.tags]
  );
  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isSwitchHospitalOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isSwitchHospitalOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const getInitialValues = (): SwitchHospitalFormValues => {
    if (popupStore.isSwitchHospitalOpen) {
      return {
        hospitalId: hospitalFilesStore.selectedHospital?.id || null,
        tagId: hospitalFilesStore.selectedTag?.id || null,
      };
    }
    return switchHospitalInitialValues;
  };

  const handleSubmit = async (values: SwitchHospitalFormValues) => {
    if (!values.hospitalId || !values.tagId) {
      return;
    }

    const selectedHospital = hospitalStore.hospitalList.find(h => h.id === values.hospitalId);
    const selectedTag = hospitalFilesStore.tags.find(tag => tag.id === values.tagId);

    if (selectedHospital && selectedTag) {
      hospitalFilesStore.setSelectedHospital(selectedHospital);
      hospitalFilesStore.setSelectedTag(selectedTag);
      popupStore.setIsSwitchHospitalOpen(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsSwitchHospitalOpen(false);
  };

  if (uiStore.isLoading) {
    return null;
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={SwitchHospitalSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Popup
          isOpen={popupStore.isSwitchHospitalOpen}
          onClose={handleClose}
          title="Hastane Seçimi"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <HospitalTagSync />
              <div className={styles.dropdownsContainer}>
                <FormikDropdown
                  name="hospitalId"
                  options={hospitalOptions}
                  label="Hastane Seçiniz"
                />
                <FormikDropdown
                  name="tagId"
                  options={tagOptions}
                  label="Etiket No Seçiniz"
                  disabled={!values.hospitalId}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
});

export default SwitchHospital;
